/**
 * Guided Checklist Engine — shared types and pure helpers (CIR-2026-546,
 * Epic 26 E1/E3/E4/E5/E6/E7/E8; contract signed off under CIR-2026-545).
 *
 * This module holds the SHELL/engine plumbing only: types matching the
 * live migration (20260706130000_cir_2026_546_guided_checklist_engine.sql),
 * track derivation, the service-tier read helper, and the progress
 * merge/upsert rule. It renders no register-specific content (Epics 27/28,
 * CIR-2026-547/548) and defines no content store (CIR-2026-551).
 */

/** Matches public.guided_track (migration §1). */
export type GuidedTrack = "ce" | "bsm";

/** Matches public.guided_progress_status (migration §1). */
export type GuidedProgressStatus = "not_started" | "in_progress" | "complete";

/** Matches public.guided_event_type (migration §1). */
export type GuidedEventType = "entry" | "exit" | "completion";

/** Matches public.guided_ooi_state (migration §1). */
export type GuidedOoiState = "outstanding" | "answered";

/**
 * Matches public.guided_node_type (20260707210000_cir_2026_553_guided_
 * journey_graph.sql §1). stage/step/milestone carry no authored content of
 * their own; static_subtask is a framework-defined, identical-for-every-
 * client item. Dynamic sub-tasks (Stage 3 gap risks, Stage 5 retest
 * findings) are NOT rows of this type — they are derived live from their own
 * source tables, never authored graph nodes.
 */
export type GuidedNodeType = "stage" | "step" | "static_subtask" | "milestone";

/** client_organisations.service_tier (CIR-2026-543; Epic 1 BR-16 default Lite). */
export type ServiceTier = "Lite" | "Enhanced";

export const GUIDED_TRACKS: readonly GuidedTrack[] = ["ce", "bsm"];

export function isGuidedTrack(value: unknown): value is GuidedTrack {
  return value === "ce" || value === "bsm";
}

export function isServiceTier(value: unknown): value is ServiceTier {
  return value === "Lite" || value === "Enhanced";
}

/**
 * Reads the service tier from an org record, defaulting to "Lite" for any
 * null/unrecognised value (Epic 1 BR-16; Epic 26 FR-14, UC-04 exception —
 * "an organisation with no plan assigned defaults to Lite and is still
 * routed into the engine, routing is never undefined"). Presentation-only:
 * the returned tier selects the support model, never an access boundary
 * (Epic 26 FR-15, FR-15a, BR-04).
 */
export function resolveServiceTier(value: unknown): ServiceTier {
  return isServiceTier(value) ? value : "Lite";
}

/**
 * Derives which guided track(s) an organisation's declared framework_scope
 * puts it in (Epic 26 FR-01/FR-02: CE+ references the Cyber Essentials
 * catalogue, BSM references ISO 27001:2022 — see the migration's
 * guided_check_step_track_framework() track<->framework_name binding). An
 * organisation scoped to both frameworks is in scope for both tracks; the
 * order below is stable (CE+ before BSM) so consumers render a consistent
 * default when both apply.
 */
export function deriveGuidedTracks(
  frameworkScope: readonly string[] | null | undefined,
): GuidedTrack[] {
  const scope = frameworkScope ?? [];
  const tracks: GuidedTrack[] = [];
  if (scope.includes("cyber_essentials")) tracks.push("ce");
  if (scope.includes("iso27001")) tracks.push("bsm");
  return tracks;
}

/** Ranks progress status so a merge never downgrades a further-along fact. */
const PROGRESS_STATUS_RANK: Record<GuidedProgressStatus, number> = {
  not_started: 0,
  in_progress: 1,
  complete: 2,
};

export interface GuidedProgressFact {
  status: GuidedProgressStatus;
  first_entered_at: string | null;
  completed_at: string | null;
}

/**
 * Computes the merged progress fact for an upsert (Epic 26 E3, FR-05/FR-06).
 * Pure so it is unit-testable without a live database:
 *  - status never regresses (entering a completed step again does not
 *    demote it back to in_progress — no engine action alters previously
 *    recorded progress, FR-07/BR-05);
 *  - first_entered_at is set once, on the first transition away from
 *    not_started, and never overwritten afterwards;
 *  - completed_at is set once, on the first transition to complete.
 */
export function mergeGuidedProgress(
  existing: GuidedProgressFact | null,
  target: GuidedProgressStatus,
  now: string,
): GuidedProgressFact {
  const existingRank = existing ? PROGRESS_STATUS_RANK[existing.status] : -1;
  // eslint-disable-next-line security/detect-object-injection -- safe: key is a typed enum value, not user input (CIR-2026-296)
  const targetRank = PROGRESS_STATUS_RANK[target];
  const status = targetRank > existingRank ? target : (existing?.status ?? target);

  const first_entered_at =
    existing?.first_entered_at ?? (status !== "not_started" ? now : null);

  const completed_at =
    existing?.completed_at ?? (status === "complete" ? now : null);

  return { status, first_entered_at, completed_at };
}
